import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Sparkles } from "lucide-react";
import { sanitizeHTML } from "@/helpers/sanitizeHelper";

type MarketValue = unknown;

type MarketData = {
  [key: string]: unknown;
};

type BudgetRecommendation = {
  explanation?: string | null;
  estimatedImpressions?: unknown;
  [key: string]: unknown;
};

type MetricConfig = {
  key: "totalPopulation" | "competitionLevel" | "medianHouseholdIncome";
  label: string;
  valueType?: "currency" | "integer";
};

type BudgetRecommendationInfoProps = {
  recommendation?: BudgetRecommendation | null;
  marketData?: MarketData | null;
  label?: string;
  showLabel?: boolean;
  infoAriaLabel?: string;
};

const MARKET_METRICS: MetricConfig[] = [
  {
    key: "totalPopulation",
    label: "Total Population",
    valueType: "integer",
  },
  {
    key: "competitionLevel",
    label: "Competition Level",
  },
  {
    key: "medianHouseholdIncome",
    label: "Median Household Income",
    valueType: "currency",
  },
];

const ZIP_PREVIEW_COUNT = 8;

const currencyFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

const integerFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

function getObject(value: unknown): MarketData {
  return value && typeof value === "object" ? (value as MarketData) : {};
}

function getRecommendationLocation(recommendation: BudgetRecommendation = {}) {
  const trace = getObject(recommendation.trace);
  const traceInputs = getObject(trace.inputs);
  return getObject(traceInputs.location);
}

function getMarketMetrics(recommendation: BudgetRecommendation = {}) {
  const location = getRecommendationLocation(recommendation);

  return MARKET_METRICS.map(({ key, label, valueType }) => ({
    label,
    value: formatMarketMetricValue(location[key], valueType),
  })).filter(
    ({ value }) => value !== undefined && value !== null && value !== ""
  );
}

function getNumericMetricValue(value: MarketValue) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value
    .trim()
    .replace(/[$,]/g, "")
    .replace(/\s*miles?$/i, "");
  if (!normalizedValue) {
    return null;
  }

  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function formatFlooredIntegerMetric(value: MarketValue) {
  const numericValue = getNumericMetricValue(value);
  return numericValue === null
    ? String(value).trim()
    : String(Math.floor(numericValue));
}

function formatMarketMetricValue(
  value: MarketValue,
  valueType?: MetricConfig["valueType"]
): string | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;

  if (value === "") {
    return "";
  }

  if (valueType === "integer") {
    const numericValue = getNumericMetricValue(value);
    return numericValue === null
      ? String(value).trim()
      : integerFormatter.format(Math.floor(numericValue));
  }

  if (valueType === "currency") {
    const numericValue = getNumericMetricValue(value);
    return numericValue === null
      ? String(value).trim()
      : `$${currencyFormatter.format(Math.floor(numericValue))}`;
  }

  return formatFlooredIntegerMetric(value);
}

function formatTradeAreaRadius(value: MarketValue) {
  const numericValue = getNumericMetricValue(value);
  if (numericValue === null || numericValue <= 0) {
    return "";
  }

  return `${Math.floor(numericValue)} Miles`;
}

function getZipCodes(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value];

  return values.flatMap((zipValue) => {
    if (zipValue === undefined || zipValue === null || zipValue === "") {
      return [];
    }

    return String(zipValue)
      .split(",")
      .map((zipCode) => zipCode.trim())
      .filter(Boolean);
  });
}

function formatNumber(value: unknown) {
  const numericValue = getNumericMetricValue(value as MarketValue);
  return numericValue === null
    ? String(value).trim()
    : integerFormatter.format(Math.floor(numericValue));
}

function formatImpressionRange(recommendation: BudgetRecommendation = {}) {
  const rangeValue = recommendation.estimatedImpressions;

  if (rangeValue && typeof rangeValue === "object") {
    const range = getObject(rangeValue);
    const minimum = range.min;
    const maximum = range.max;

    if (minimum !== undefined && maximum !== undefined) {
      return `${formatNumber(minimum)} - ${formatNumber(maximum)}`;
    }
  }

  if (Array.isArray(rangeValue) && rangeValue.length >= 2) {
    return `${formatNumber(rangeValue[0])} - ${formatNumber(rangeValue[1])}`;
  }

  if (rangeValue === undefined || rangeValue === null || rangeValue === "") {
    return "";
  }

  const rangeParts = String(rangeValue).split(/\s*(?:-|–|—|to)\s*/i);
  return rangeParts.length === 2
    ? `${formatNumber(rangeParts[0])} - ${formatNumber(rangeParts[1])}`
    : formatNumber(rangeValue);
}

function getLocationLabel(
  recommendation: BudgetRecommendation = {},
  marketData: MarketData = {}
) {
  const location = getRecommendationLocation(recommendation);
  const primaryLocation =
    location.locationName ||
    location.city ||
    marketData.location_name ||
    marketData.city;

  return [
    primaryLocation,
    location.state || marketData.state,
    marketData.country,
  ]
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map(String)
    .join(", ");
}

function stripHtmlCodeFence(value: string) {
  return value
    .trim()
    .replace(/^```(?:html)?\s*/i, "")
    .replace(/\s*```$/i, "");
}

function BudgetRecommendationTooltip({
  recommendation = {},
  marketData = {},
  onClose,
}: Pick<BudgetRecommendationInfoProps, "recommendation" | "marketData"> & {
  onClose: () => void;
}) {
  const [zipListExpanded, setZipListExpanded] = useState(false);
  const location = getRecommendationLocation(recommendation);
  const metrics = getMarketMetrics(recommendation);
  const radius = formatTradeAreaRadius(location.tradeAreaRadiusMiles);
  const zipCodes = getZipCodes(location.zipCode);
  const hasLongZipList = zipCodes.length > ZIP_PREVIEW_COUNT;
  const visibleZipCodes =
    hasLongZipList && !zipListExpanded
      ? `${zipCodes.slice(0, ZIP_PREVIEW_COUNT).join(", ")}…`
      : zipCodes.join(", ");
  const locationLabel = getLocationLabel(recommendation, marketData);
  const impressionRange = formatImpressionRange(recommendation);
  const explanation = recommendation?.explanation
    ? sanitizeHTML(stripHtmlCodeFence(recommendation.explanation))
    : "";
  const hasMarketIntelligence = Boolean(
    radius || zipCodes.length || metrics.length
  );
  const metricCardCount = metrics.length + (!zipCodes.length && radius ? 1 : 0);

  return (
    <Box className="budget-recommendation-tooltip">
      <Box className="budget-recommendation-tooltip-header">
        <Box className="budget-recommendation-tooltip-title-group">
          <Typography
            className="budget-recommendation-tooltip-title"
            component="h3"
          >
            Budget Recommendation Information
          </Typography>
          {locationLabel ? (
            <Typography className="budget-recommendation-tooltip-location">
              <strong>Location:</strong> {locationLabel}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          aria-label="Close budget recommendation details"
          className="budget-recommendation-tooltip-close"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {impressionRange ? (
        <Box className="budget-recommendation-tooltip-section budget-recommendation-tooltip-impressions">
          <Typography
            className="budget-recommendation-tooltip-heading"
            component="h4"
          >
            Estimated Impressions
          </Typography>
          <Box className="budget-recommendation-tooltip-impression-value">
            {impressionRange}
          </Box>
        </Box>
      ) : null}

      {explanation ? (
        <Box className="budget-recommendation-tooltip-section budget-recommendation-tooltip-explanation">
          <Typography
            className="budget-recommendation-tooltip-heading"
            component="h4"
          >
            Why This Budget?
          </Typography>
          <Typography
            className="budget-recommendation-tooltip-copy"
            variant="body2"
            dangerouslySetInnerHTML={{ __html: explanation }}
          />
        </Box>
      ) : null}

      {hasMarketIntelligence ? (
        <Box className="budget-recommendation-tooltip-section budget-recommendation-tooltip-metrics">
          <Typography
            className="budget-recommendation-tooltip-heading"
            component="h4"
          >
            Market Intelligence
          </Typography>

          {zipCodes.length ? (
            <Box className="budget-recommendation-tooltip-metric budget-recommendation-tooltip-trade-area">
              <Typography component="span" variant="caption">
                {radius
                  ? "Trade Area Radius + Zip codes"
                  : "Trade Area Zip codes"}
              </Typography>
              {radius ? (
                <Typography component="strong" variant="body2">
                  {radius}
                </Typography>
              ) : null}
              <Box className="budget-recommendation-tooltip-zip-row">
                <Typography
                  className="budget-recommendation-tooltip-zip-list"
                  component="strong"
                  variant="body2"
                >
                  {visibleZipCodes}
                </Typography>
                {hasLongZipList ? (
                  <button
                    aria-expanded={zipListExpanded}
                    aria-label={
                      zipListExpanded
                        ? "Collapse zip code list"
                        : "See full zip code list"
                    }
                    className="budget-recommendation-tooltip-zip-toggle"
                    onClick={() => setZipListExpanded((expanded) => !expanded)}
                    type="button"
                  >
                    {zipListExpanded ? "Show less" : "See full list"}
                    {zipListExpanded ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </button>
                ) : null}
              </Box>
            </Box>
          ) : null}

          {metrics.length || (!zipCodes.length && radius) ? (
            <Box
              className={`budget-recommendation-tooltip-metric-grid${
                !zipCodes.length && radius ? " is-radius-only" : ""
              } has-${metricCardCount}-metrics`}
              role="list"
            >
              {!zipCodes.length && radius ? (
                <Box
                  className="budget-recommendation-tooltip-metric"
                  role="listitem"
                >
                  <Typography component="span" variant="caption">
                    Trade Area Radius
                  </Typography>
                  <Typography component="strong" variant="body2">
                    {radius}
                  </Typography>
                </Box>
              ) : null}
              {metrics.map((metric) => (
                <Box
                  className="budget-recommendation-tooltip-metric"
                  key={metric.label}
                  role="listitem"
                >
                  <Typography component="span" variant="caption">
                    {metric.label}
                  </Typography>
                  <Typography component="strong" variant="body2">
                    {metric.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

export default function BudgetRecommendationInfo({
  recommendation,
  marketData,
  label = "Budget Recommendation",
  showLabel = true,
  infoAriaLabel = "View budget recommendation details",
}: BudgetRecommendationInfoProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const closeTooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const clearTooltipCloseTimer = () => {
    if (closeTooltipTimerRef.current) {
      clearTimeout(closeTooltipTimerRef.current);
      closeTooltipTimerRef.current = null;
    }
  };

  const handleOpen = () => {
    clearTooltipCloseTimer();
    setTooltipOpen(true);
  };

  const handleClose = () => {
    clearTooltipCloseTimer();
    setTooltipOpen(false);
  };

  const scheduleClose = () => {
    clearTooltipCloseTimer();
    closeTooltipTimerRef.current = setTimeout(() => {
      setTooltipOpen(false);
      closeTooltipTimerRef.current = null;
    }, 200);
  };

  useEffect(
    () => () => {
      if (closeTooltipTimerRef.current) {
        clearTimeout(closeTooltipTimerRef.current);
      }
    },
    []
  );

  if (!recommendation && !showLabel) {
    return null;
  }

  const handleInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    clearTooltipCloseTimer();
    setTooltipOpen((open) => !open);
  };

  return (
    <div className="budget-recommendation-label">
      {showLabel ? (
        <span style={{ borderWidth: 0 }}>
          <Sparkles size={14} strokeWidth={1.8} />
          {label}
        </span>
      ) : null}
      {recommendation ? (
        <Tooltip
          open={tooltipOpen}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement="top-end"
          title={
            <BudgetRecommendationTooltip
              recommendation={recommendation}
              marketData={marketData || {}}
              onClose={handleClose}
            />
          }
          slotProps={{
            popper: {
              disablePortal: false,
              modifiers: [
                {
                  name: "flip",
                  enabled: false,
                },
                {
                  name: "preventOverflow",
                  options: {
                    altAxis: true,
                    padding: 12,
                    rootBoundary: "viewport",
                    tether: false,
                  },
                },
              ],
              onMouseEnter: handleOpen,
              onMouseLeave: scheduleClose,
              style: { pointerEvents: "auto", zIndex: 99999 },
            },
            tooltip: {
              sx: {
                boxSizing: "border-box",
                width: {
                  xs: "min(500px, calc(100vw - 24px))",
                  sm: 500,
                },
                maxHeight: {
                  xs: "calc(100dvh - 24px)",
                  sm: "min(560px, 78vh)",
                },
                maxWidth: "calc(100vw - 24px)",
                overflowX: "hidden",
                overflowY: "auto",
                overscrollBehavior: "contain",
                scrollbarWidth: "thin",
                backgroundColor: "#ffffff",
                color: "#0f3447",
                padding: 0,
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.16)",
                border: "1px solid #e2e6ea",
              },
            },
          }}
        >
          <IconButton
            aria-label={infoAriaLabel}
            className="budget-recommendation-info"
            disableFocusRipple
            disableRipple
            size="small"
            onClick={handleInfoClick}
            onMouseEnter={handleOpen}
            onMouseLeave={scheduleClose}
            sx={{
              backgroundColor: "transparent",
              color: "#0a58b5",
              height: 24,
              padding: "2px",
              width: 24,
              "&:hover, &:focus-visible": {
                backgroundColor: "transparent",
                color: "#0a58b5",
              },
              "& .MuiSvgIcon-root": {
                color: "#0a58b5",
                fill: "currentColor",
                fontSize: 17,
              },
            }}
          >
            <InfoOutlinedIcon
              data-testid="budget-recommendation-info-icon"
              sx={{ color: "#0a58b5", fontSize: 17 }}
            />
          </IconButton>
        </Tooltip>
      ) : null}
    </div>
  );
}
